import React, { useState } from 'react'
import { reactular } from '@vendhq/reactular'
import {
  Button,
  FieldLabel,
  GridColumn,
  GridRow,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
  CurrencyInput,
  NumberInputValidators,
} from '@lightspeed/design-system-react'
import { vn } from 'vend-number'
import { trackUserEvent } from '@vend/utilities'
import ReactDOM from 'react-dom'
import { $rootScope } from '../../../ng-compat/imports'
import { useAppDispatch, useAppSelector } from '../../../state/hooks'
import {
  removePayAccountBalance,
  selectCurrentSale,
  updatePayAccountBalanceAmount,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { useSaleManager } from '../../../ng-compat/hooks'
import { EVENT } from '../../../constants'
import { WEB_REGISTER } from '../../web-register.constant'

interface Props {
  close: () => void
}

export const AccountBalanceSetupModal: React.FunctionComponent<Props> = ({
  close,
}) => {
  const sale = useAppSelector(selectCurrentSale)
  const accountBalance = sale.payAccountBalance
  const saleCustomer = sale.customer
  const customerBalances = sale.customerBalances
  const dispatch = useAppDispatch()
  const saleManager = useSaleManager()

  // Customer account balance
  // `balance` is in negative so creating an absolute value from it
  const currentBalance = vn(customerBalances?.account).abs().toNumber()
  // Worst case scenorio
  const customer = saleCustomer?.name || saleCustomer?.email || 'This customer'
  // Initial selected amount
  const amount = accountBalance?.amount

  // Input field validation rules
  const validators: NumberInputValidators = {
    nonNegative: true,
    nonEmpty: true,
    nonZero: true,
    max: currentBalance,
    decimalPlaces: 2,
  }
  // Holds the value of the input field
  // Default will be the passed inital amount or account balance
  const [selectedAmount, setSelectedAmount] = useState<number | string>(
    amount || currentBalance
  )
  // Holds the input field validation state
  const [valid, setValid] = useState<boolean>(true)
  // Wrapper function for the angular close
  // This will return a valid `PayAccountBalance` type if the
  // `selectedAmountToPay` is not undefined
  const closeModal = async (
    selectedAmountToPay: number | string | undefined
  ) => {
    let currentAction = amount ? 'Edit' : 'Add'

    if (typeof selectedAmountToPay !== 'undefined') {
      dispatch(updatePayAccountBalanceAmount(selectedAmountToPay))
    } else {
      dispatch(removePayAccountBalance())
      currentAction = 'Remove'
    }

    await saleManager.saveSale()

    $rootScope.$emit(EVENT.SALE_UPDATE_TENDERED_AMOUNT)
    trackUserEvent(
      WEB_REGISTER.DISPLAY_NAME,
      'Clicked: Account Balance Payment',
      { action: currentAction, saleId: sale.localId }
    )

    close()
  }
  // Blur handler for the input field
  // This will convert the input value to 2 dp
  const handleBlur = (value: number | string) => {
    setSelectedAmount(vn(value).toFixed(2))
  }

  return ReactDOM.createPortal(
    <Modal
      onClose={() => {
        closeModal(amount)
      }}
      className="vd-product-modal"
    >
      <ModalHeader>Add an Account Balance payment.</ModalHeader>
      <ModalContent>
        <p>
          {customer} has unpaid On Account and Layby sales totalling $
          {currentBalance}. The oldest sales will be paid off first.
        </p>
        <GridRow>
          <GridColumn small="12/12" medium="5/12">
            <FieldLabel label="Amount to Pay">
              <CurrencyInput
                onChange={() => {
                  setValid(true)
                }}
                onBlur={handleBlur}
                value={selectedAmount}
                placeholder="0.00"
                validators={validators}
                onError={() => {
                  setValid(false)
                }}
              />
            </FieldLabel>
          </GridColumn>
        </GridRow>
      </ModalContent>
      <ModalActions>
        <div className="vd-btn-group">
          {/* Add mode */}
          {!amount && (
            <>
              <Button
                variant="supplementary"
                onClick={() => {
                  // Send the passed amount and let parent deal with it
                  closeModal(amount)
                }}
              >
                Don't Add Payment
              </Button>
              <Button
                variant="go"
                disabled={!valid}
                onClick={() => {
                  closeModal(selectedAmount)
                }}
                data-cy="add-account-balance-payment-button"
              >
                Add Payment
              </Button>
            </>
          )}
          {/* Edit/update mode */}
          {amount && (
            <>
              <Button
                variant="supplementary"
                onClick={() => {
                  closeModal(undefined)
                }}
                data-cy="remove-account-balance-payment-button"
              >
                Remove Payment
              </Button>
              <Button
                variant="supplementary"
                onClick={() => {
                  // Send the passed amount and let parent deal with it
                  closeModal(amount)
                }}
              >
                Don't Update Payment
              </Button>
              <Button
                variant="go"
                disabled={!valid}
                onClick={() => {
                  closeModal(selectedAmount)
                }}
                data-cy="update-account-balance-payment-button"
              >
                Update Payment
              </Button>
            </>
          )}
        </div>
      </ModalActions>
    </Modal>,
    document.body
  )
}

export const AccountBalanceSetupModalComponent = reactular(
  AccountBalanceSetupModal,
  ['close'],
  'reactWrapper'
)
